import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, tap } from 'rxjs';
import { QuestionStateModel } from '../models/question-state.model';
import { GeneralQuestion, QuestionRequirements, Questions } from '../models/question.model';
import { QuestionsService } from '../services/questions.service';
import { filterMatchingQuestions } from '../utils/question.util';
import {
    AddCustomQuestion,
    AddSelectionToInterview,
    ClearActiveSelection,
    ClearInterviewerQuestions,
    EditCustomQuestion,
    LoadQuestionBank,
    RemoveCustomQuestion,
    RemoveQuestionFromInterview,
    RemoveSelectionFromInterview,
    ToggleQuestionChecked,
    UpdateSelection
} from './question.actions';

@State<QuestionStateModel>({
    name: 'question',
    defaults: {
        selections: {
            leadership: { leadershipLeader: [], leadershipCompetency: [], leadershipRole: [] },
            supplement: { supplementCompetency: [], supplementRole: [] },
            values: { valuesCompetency: [], valuesRole: [] },
            principles: { principlesHS: [] }
        },
        questions: { leadership: [], supplement: [], principles: [], values: [] },
        interviewerQuestions: {}
    }
})
@Injectable()
export class QuestionState {
    private readonly questionsService = inject(QuestionsService);

    @Selector()
    static valuesSelections(state: QuestionStateModel): Record<string, string[]> {
        return state.selections.values;
    }

    @Selector()
    static leadershipSelections(state: QuestionStateModel): Record<string, string[]> {
        return state.selections.leadership;
    }

    @Selector()
    static supplementSelections(state: QuestionStateModel): Record<string, string[]> {
        return state.selections.supplement;
    }

    @Selector()
    static principlesSelections(state: QuestionStateModel): Record<string, string[]> {
        return state.selections.principles;
    }

    // --- Individual field selectors (for disabled checks / two-way binding) ---

    @Selector()
    static valuesCompetency(state: QuestionStateModel): string[] {
        return state.selections.values.valuesCompetency;
    }

    @Selector()
    static valuesRole(state: QuestionStateModel): string[] {
        return state.selections.values.valuesRole;
    }

    @Selector()
    static leadershipLeader(state: QuestionStateModel): string[] {
        return state.selections.leadership.leadershipLeader;
    }

    @Selector()
    static leadershipCompetency(state: QuestionStateModel): string[] {
        return state.selections.leadership.leadershipCompetency;
    }

    @Selector()
    static leadershipRole(state: QuestionStateModel): string[] {
        return state.selections.leadership.leadershipRole;
    }

    @Selector()
    static supplementCompetency(state: QuestionStateModel): string[] {
        return state.selections.supplement.supplementCompetency;
    }

    @Selector()
    static supplementRole(state: QuestionStateModel): string[] {
        return state.selections.supplement.supplementRole;
    }

    @Selector()
    static principlesHS(state: QuestionStateModel): string[] {
        return state.selections.principles.principlesHS;
    }

    // --- Questions Selectors ---

    @Selector()
    static questions(state: QuestionStateModel) {
        return state.questions;
    }

    @Selector()
    static valuesQuestionsLength(state: QuestionStateModel): number {
        return filterMatchingQuestions(state.questions.values, state.selections.values).length;
    }

    @Selector()
    static leadershipQuestionsLength(state: QuestionStateModel): number {
        return filterMatchingQuestions(state.questions.leadership, state.selections.leadership).length;
    }

    @Selector()
    static supplementQuestionsLength(state: QuestionStateModel): number {
        return filterMatchingQuestions(state.questions.supplement, state.selections.supplement).length;
    }

    @Selector()
    static principlesQuestionsLength(state: QuestionStateModel): number {
        return filterMatchingQuestions(state.questions.principles, state.selections.principles).length;
    }

    // --- Interview Selectors ---

    @Selector()
    static interviewerQuestions(state: QuestionStateModel): Record<number, GeneralQuestion[]> {
        return state.interviewerQuestions;
    }

    @Selector()
    static selectedItemsCount(state: QuestionStateModel): number {
        const questions = state.questions;
        return (
            questions.leadership.filter(item => item.checked).length +
            questions.supplement.filter(item => item.checked).length +
            questions.principles.filter(item => item.checked).length +
            questions.values.filter(item => item.checked).length
        );
    }

    @Selector()
    static hasInterviewerQuestions(state: QuestionStateModel): boolean {
        for (let index = 1; index < 7; index++) {
            if (state.interviewerQuestions[index]?.length > 0) {
                return true;
            }
        }
        return false;
    }

    @Action(LoadQuestionBank)
    loadQuestionBank(ctx: StateContext<QuestionStateModel>): Observable<Questions> {
        return this.questionsService.getQuestionBank().pipe(
            tap(questions => {
                ctx.patchState({ questions: this.initializeQuestions(questions) });
            })
        );
    }

    @Action(UpdateSelection)
    updateSelection(ctx: StateContext<QuestionStateModel>, { category, field, values }: UpdateSelection): void {
        const state = ctx.getState();
        ctx.patchState({
            selections: {
                ...state.selections,
                [category]: {
                    ...state.selections[category as keyof typeof state.selections],
                    [field]: values
                }
            }
        });
    }

    @Action(ToggleQuestionChecked)
    toggleQuestionChecked(
        ctx: StateContext<QuestionStateModel>,
        { category, questionId }: ToggleQuestionChecked
    ): void {
        const state = ctx.getState();
        const categoryQuestions = state.questions[category as keyof Questions];
        const updated = categoryQuestions.map(question =>
            question.id === questionId ? { ...question, checked: !question.checked } : question
        );
        ctx.patchState({
            questions: {
                ...state.questions,
                [category]: updated
            }
        });
    }

    @Action(AddSelectionToInterview)
    addSelectionToInterview(ctx: StateContext<QuestionStateModel>, { interviewerNum }: AddSelectionToInterview): void {
        const state = ctx.getState();
        const selectedItems = this.getSelectedItems(state);
        const existing = state.interviewerQuestions[interviewerNum] || [];
        const merged = [...existing, ...selectedItems];
        const unique = merged.filter((item, index) => merged.findIndex(_item => _item.id === item.id) === index);
        ctx.patchState({
            interviewerQuestions: {
                ...state.interviewerQuestions,
                [interviewerNum]: unique
            }
        });
    }

    @Action(RemoveSelectionFromInterview)
    removeSelectionFromInterview(
        ctx: StateContext<QuestionStateModel>,
        { interviewerNum }: RemoveSelectionFromInterview
    ): void {
        const state = ctx.getState();
        const selectedIds = new Set(this.getSelectedItems(state).map(question => question.id));
        ctx.patchState({
            interviewerQuestions: {
                ...state.interviewerQuestions,
                [interviewerNum]: (state.interviewerQuestions[interviewerNum] || []).filter(
                    question => !selectedIds.has(question.id)
                )
            }
        });
    }

    @Action(ClearActiveSelection)
    clearActiveSelection(ctx: StateContext<QuestionStateModel>): void {
        const state = ctx.getState();
        const questions = { ...state.questions };
        for (const key of Object.keys(questions) as Array<keyof typeof questions>) {
            questions[key] = questions[key].map(question => ({ ...question, checked: false }));
        }
        ctx.patchState({ questions });
    }

    @Action(ClearInterviewerQuestions)
    clearInterviewerQuestions(
        ctx: StateContext<QuestionStateModel>,
        { interviewerNum }: ClearInterviewerQuestions
    ): void {
        const state = ctx.getState();
        ctx.patchState({
            interviewerQuestions: {
                ...state.interviewerQuestions,
                [interviewerNum]: []
            }
        });
    }

    @Action(AddCustomQuestion)
    addCustomQuestion(ctx: StateContext<QuestionStateModel>, { category, text }: AddCustomQuestion): void {
        const state = ctx.getState();
        const categoryQuestions = state.questions[category as keyof Questions];
        const newQuestion: GeneralQuestion = {
            id: `custom-${Date.now()}`,
            type: category as GeneralQuestion['type'],
            question: text,
            checked: false,
            custom: true,
            requirements: {} as QuestionRequirements
        };
        ctx.patchState({
            questions: {
                ...state.questions,
                [category]: [...categoryQuestions, newQuestion]
            }
        });
    }

    @Action(RemoveQuestionFromInterview)
    removeQuestionFromInterview(
        ctx: StateContext<QuestionStateModel>,
        { interviewerNum, questionId }: RemoveQuestionFromInterview
    ): void {
        const state = ctx.getState();
        const questions = state.interviewerQuestions[interviewerNum] || [];
        ctx.patchState({
            interviewerQuestions: {
                ...state.interviewerQuestions,
                [interviewerNum]: questions.filter(question => question.id !== questionId)
            }
        });
    }

    @Action(EditCustomQuestion)
    editCustomQuestion(
        ctx: StateContext<QuestionStateModel>,
        { category, questionId, text }: EditCustomQuestion
    ): void {
        const state = ctx.getState();
        const categoryQuestions = state.questions[category as keyof Questions];
        const interviewerQuestions = { ...state.interviewerQuestions };
        for (const key of Object.keys(interviewerQuestions)) {
            const num = Number(key);
            interviewerQuestions[num] = interviewerQuestions[num].map(question =>
                question.id === questionId ? { ...question, question: text } : question
            );
        }
        ctx.patchState({
            questions: {
                ...state.questions,
                [category]: categoryQuestions.map(question =>
                    question.id === questionId ? { ...question, question: text } : question
                )
            },
            interviewerQuestions
        });
    }

    @Action(RemoveCustomQuestion)
    removeCustomQuestion(ctx: StateContext<QuestionStateModel>, { category, questionId }: RemoveCustomQuestion): void {
        const state = ctx.getState();
        const categoryQuestions = state.questions[category as keyof Questions];
        const interviewerQuestions = { ...state.interviewerQuestions };
        for (const key of Object.keys(interviewerQuestions)) {
            const num = Number(key);
            interviewerQuestions[num] = interviewerQuestions[num].filter(question => question.id !== questionId);
        }
        ctx.patchState({
            questions: {
                ...state.questions,
                [category]: categoryQuestions.filter(question => question.id !== questionId)
            },
            interviewerQuestions
        });
    }

    private getSelectedItems(state: QuestionStateModel): GeneralQuestion[] {
        const question = state.questions;
        return [
            ...question.leadership.filter(item => item.checked),
            ...question.supplement.filter(item => item.checked),
            ...question.principles.filter(item => item.checked),
            ...question.values.filter(item => item.checked)
        ];
    }

    private initializeQuestions(questions: Questions): Questions {
        return {
            leadership: questions.leadership.map(question => ({ ...question, checked: !!question.checked })),
            supplement: questions.supplement.map(question => ({ ...question, checked: !!question.checked })),
            principles: questions.principles.map(question => ({ ...question, checked: !!question.checked })),
            values: questions.values.map(question => ({ ...question, checked: !!question.checked }))
        };
    }
}
